import { api } from '../lib/api'
import { useGatedBook } from './useGatedBook'
import { REGISTER_PATH } from './useRegisterBook'
import { FEED_PATH } from './useRoundsFeed'
import type { RegisterEntry } from './useRegisterBook'
import type { FeedRound } from './useRoundsFeed'

/**
 * What the clubhouse home reads out of the golf SCORING API: the club's book of
 * posted rounds (`GET /golf/rounds/feed`) and the club register
 * (`GET /golf/register`), fetched together because the page is one surface and
 * the two endpoints share one gate.
 *
 * ONE read of two endpoints, deliberately: `Promise.all` inside a single
 * `useGatedBook`, so the page has one gate, one loading state and one refusal
 * rather than two that can disagree about whether the clubhouse is open. Either
 * endpoint refusing is the club refusing.
 *
 * Both are membership-gated (Fireside::MembershipGate). A 403
 * `membership_required` is therefore NOT an error — it is the club's answer,
 * and `membershipRequired` is what the screen renders its own gate from. Every
 * clubhouse PAGE stays ungated on purpose so a lapsed member is never trapped;
 * the standing is enforced on the data, not on the door.
 *
 * Fetches only while `enabled` (the viewer is signed in) — a visitor's console
 * gets a sign-in invitation, not a 401.
 */

/**
 * The feed's own shapes (#round_feed_json, #feed_player_json,
 * #shared_member_json).
 *
 * Owned by `useRoundsFeed`, which is the hook for the pages that read the book
 * ALONE. Re-exported here so the clubhouse home keeps importing its two shapes
 * from one place, and so there is exactly ONE declaration of the wire.
 */
export type { FeedPlayer, FeedRound, FeedScorekeeper } from './useRoundsFeed'

/**
 * One line of the club register (#register_entry_json).
 *
 * Owned by `useRegisterBook`, which is the hook for the page that renders the
 * whole book. Re-exported here so the clubhouse home keeps importing its two
 * shapes from one place, and so there is exactly ONE declaration of the wire.
 */
export type { RegisterEntry }

interface RoundsFeedResponse {
  rounds: FeedRound[]
}
interface RegisterResponse {
  register: RegisterEntry[]
}

/** The clubhouse home's one read, in the shape the page renders. */
interface ClubhouseBook {
  rounds: FeedRound[]
  register: RegisterEntry[]
}

/** One empty book, not a fresh one per render — see `useGatedBook`'s note. */
const NO_ROUNDS: FeedRound[] = []
const NO_REGISTER: RegisterEntry[] = []

export function useClubhouseBook(enabled: boolean) {
  const book = useGatedBook<ClubhouseBook>({
    key: `${FEED_PATH}+${REGISTER_PATH}`,
    enabled,
    read: () =>
      Promise.all([
        api.get<RoundsFeedResponse>(FEED_PATH),
        api.get<RegisterResponse>(REGISTER_PATH),
      ]).then(([feed, register]) => ({ rounds: feed.rounds, register: register.register })),
    whenFaulty: 'Could not open the club’s book.',
  })

  return {
    rounds: book.data?.rounds ?? NO_ROUNDS,
    register: book.data?.register ?? NO_REGISTER,
    isLoading: book.isLoading,
    error: book.error,
    membershipRequired: book.membershipRequired,
    refresh: book.refresh,
  }
}
